import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = "https://gaurav-o0un.onrender.com/api";

const SalesmanForm = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    father_name: '',
    phone: '',
    email: '',
    address: '',
    city_state: '',
    education: '',
    experience_years: '',
    previous_company: '',
    area_preference: '',
    expected_salary: '',
    joining_date: '',
  });
  const [files, setFiles] = useState({
    resume: null,
    aadhar_card: null,
    experience_certificate: null,
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    // Phone validation - 10 digits
    if (!/^\d{10}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    
    // Email validation
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    setFiles(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error(t('error'), { description: 'Please verify the CAPTCHA' });
      return;
    }

    if (!validateForm()) {
      toast.error(t('error'), { description: 'Please fix the errors in the form' });
      return;
    }

    setIsSubmitting(true);
    
    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      submitData.append(key, value);
    });
    submitData.append('captcha_verified', captchaVerified);
    
    if (files.resume) submitData.append('resume', files.resume);
    if (files.aadhar_card) submitData.append('aadhar_card', files.aadhar_card);
    if (files.experience_certificate) submitData.append('experience_certificate', files.experience_certificate);

    try {
      await axios.post(`${API}/salesman-application`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('success'), { description: 'Application submitted successfully!' });
      setFormData({
        full_name: '', father_name: '', phone: '', email: '', address: '',
        city_state: '', education: '', experience_years: '', previous_company: '',
        area_preference: '', expected_salary: '', joining_date: '',
      });
      setFiles({ resume: null, aadhar_card: null, experience_certificate: null });
      setCaptchaVerified(false);
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Submission failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="salesman" className="section-spacing" data-testid="salesman-section">
      <div className="container-custom">
        <h2 
          className="text-3xl md:text-5xl font-bold text-[#0047AB] text-center mb-4"
          data-testid="salesman-title"
        >
          {t('salesmanTitle')}
        </h2>
        <div className="section-divider mb-12"></div>

        <form onSubmit={handleSubmit} className="form-container" data-testid="salesman-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name">{t('fullName')} *</Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-fullname"
              />
            </div>

            {/* Father's Name */}
            <div className="space-y-2">
              <Label htmlFor="father_name">{t('fatherName')} *</Label>
              <Input
                id="father_name"
                name="father_name"
                value={formData.father_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-fathername"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">{t('phone')} *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                required
                maxLength={10}
                pattern="\d{10}"
                className={`input-field ${errors.phone ? 'border-red-500' : ''}`}
                data-testid="salesman-phone"
              />
              {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')} *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                data-testid="salesman-email"
              />
              {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
            </div>

            {/* Address */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">{t('address')} *</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-address"
              />
            </div>

            {/* City/State */}
            <div className="space-y-2">
              <Label htmlFor="city_state">{t('cityState')} *</Label>
              <Input
                id="city_state"
                name="city_state"
                value={formData.city_state}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-citystate"
              />
            </div>

            {/* Education */}
            <div className="space-y-2">
              <Label htmlFor="education">{t('education')} *</Label>
              <Input
                id="education"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-education"
              />
            </div>

            {/* Years of Experience */}
            <div className="space-y-2">
              <Label htmlFor="experience_years">{t('yearsExperience')} *</Label>
              <Input
                id="experience_years"
                name="experience_years"
                value={formData.experience_years}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-experience"
              />
            </div>

            {/* Previous Company */}
            <div className="space-y-2">
              <Label htmlFor="previous_company">{t('previousCompany')}</Label>
              <Input
                id="previous_company"
                name="previous_company"
                value={formData.previous_company}
                onChange={handleInputChange}
                className="input-field"
                data-testid="salesman-prevcompany"
              />
            </div>

            {/* Area Preference */}
            <div className="space-y-2">
              <Label htmlFor="area_preference">{t('areaPreference')} *</Label>
              <Input
                id="area_preference"
                name="area_preference"
                value={formData.area_preference}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-areapref"
              />
            </div>

            {/* Expected Salary */}
            <div className="space-y-2">
              <Label htmlFor="expected_salary">{t('expectedSalary')} *</Label>
              <Input
                id="expected_salary"
                name="expected_salary"
                value={formData.expected_salary}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-salary"
              />
            </div>

            {/* Joining Date */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="joining_date">{t('joiningDate')} *</Label>
              <Input
                id="joining_date"
                name="joining_date"
                type="date"
                value={formData.joining_date}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="salesman-joiningdate"
              />
            </div>

            {/* File Uploads */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Resume */}
              <div className="space-y-2">
                <Label>{t('uploadResume')}</Label>
                <div className="file-upload border-2 border-dashed border-slate-300 p-4 text-center hover:border-[#0047AB] transition-colors">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{files.resume?.name || 'Click to upload'}</p>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'resume')}
                    data-testid="salesman-resume-upload"
                  />
                </div>
              </div>

              {/* Aadhar */}
              <div className="space-y-2">
                <Label>{t('uploadAadhar')}</Label>
                <div className="file-upload border-2 border-dashed border-slate-300 p-4 text-center hover:border-[#0047AB] transition-colors">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{files.aadhar_card?.name || 'Click to upload'}</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'aadhar_card')}
                    data-testid="salesman-aadhar-upload"
                  />
                </div>
              </div>

              {/* Experience Certificate */}
              <div className="space-y-2">
                <Label>{t('uploadExpCert')}</Label>
                <div className="file-upload border-2 border-dashed border-slate-300 p-4 text-center hover:border-[#0047AB] transition-colors">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{files.experience_certificate?.name || 'Click to upload'}</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'experience_certificate')}
                    data-testid="salesman-expcert-upload"
                  />
                </div>
              </div>
            </div>

            {/* CAPTCHA */}
            <div className="md:col-span-2">
              <div className="captcha-container">
                <Checkbox
                  id="captcha"
                  checked={captchaVerified}
                  onCheckedChange={setCaptchaVerified}
                  data-testid="salesman-captcha"
                />
                <Label htmlFor="captcha" className="cursor-pointer">{t('captchaVerify')}</Label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2">
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={isSubmitting}
                data-testid="salesman-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('submitting')}
                  </>
                ) : (
                  t('submit')
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default SalesmanForm;
